import React, { useState } from "react";
import { Input } from "./input"; // Shandcn UI Input
import { useMask } from "@react-input/mask";

import { DateTime } from "luxon";
import { CalendarIcon, CircleX } from "lucide-react";
import { Calendar } from "./calendar";
import { Popover, PopoverAnchor, PopoverContent } from "./popover";
import { Button } from "./button";
import { cn } from "@/lib/utils";

interface DateTimeRangePickerProps {
    range?: { from: DateTime; to: DateTime };
    onRangeChange?: (range?: { from: DateTime; to: DateTime }) => void;
    placeholder?: string;
    className?: string;
    allowClear?: boolean;
    disabled?: boolean;
}

export default function DateRangePicker({ range, onRangeChange, placeholder = "Sélectionner une plage de dates", className, allowClear, disabled }: DateTimeRangePickerProps) {
    const [open, setOpen] = React.useState(false);
    const fromValue = range?.from.isValid ? range.from.toFormat("dd/MM/yyyy") : "";
    const toValue = range?.to.isValid ? range.to.toFormat("dd/MM/yyyy") : "";

    const [selectedRange, setSelectedRange] = useState<{ from?: DateTime; to?: DateTime } | undefined>(range);
    const [month, setMonth] = React.useState<Date | undefined>(range?.from?.toJSDate());

    const [inputValue, setInputValue] = useState(range ? `${fromValue} - ${toValue}` : undefined);

    const inputRef = useMask({ mask: "__/__/____ - __/__/____", replacement: { _: /\d/ }, showMask: true });

    return (
        <Popover open={open} onOpenChange={setOpen}>
            {!open ? (
                <Button
                    variant={"outline"}
                    className={cn("items-center justify-between text-left font-normal", !range && "text-muted-foreground", className)}
                    onClick={() => setOpen(true)}
                    disabled={disabled}
                >
                    <span className="flex justify-start">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {selectedRange && selectedRange.from && selectedRange.to ? (
                            selectedRange.from.toFormat("dd/MM/yyyy") + " - " + selectedRange.to.toFormat("dd/MM/yyyy")
                        ) : (
                            <span>{placeholder}</span>
                        )}
                    </span>
                    <span>
                        {allowClear && selectedRange && (
                            <CircleX
                                className="h-4 w-4 cursor-pointer"
                                onClick={e => {
                                    setSelectedRange(undefined);
                                    if (onRangeChange) onRangeChange(undefined);
                                    e.stopPropagation();
                                }}
                            />
                        )}
                    </span>
                </Button>
            ) : (
                <Input
                    ref={inputRef}
                    value={inputValue}
                    onChange={e => {
                        setInputValue(e.target.value);
                        try {
                            const [fromString, toString] = e.target.value.split(" - ");

                            const modifiedFromDay = DateTime.fromFormat(fromString, "dd/MM/yyyy");
                            const modifiedToDay = DateTime.fromFormat(toString, "dd/MM/yyyy");

                            const fromD = modifiedFromDay.isValid ? modifiedFromDay : undefined;
                            const toD = modifiedToDay.isValid ? modifiedToDay : undefined;

                            if (fromD && toD) {
                                setSelectedRange({
                                    from: modifiedFromDay,
                                    to: modifiedToDay
                                });
                                if (onRangeChange) {
                                    onRangeChange({
                                        from: modifiedFromDay,
                                        to: modifiedToDay
                                    });
                                }
                            } else if (!fromD && !toD) {
                                setSelectedRange(undefined);
                                if (onRangeChange) onRangeChange(undefined);
                            }

                            if (modifiedFromDay.isValid && modifiedToDay.isValid) setMonth(modifiedFromDay.toJSDate());
                        } catch (error) {}
                    }}
                    onKeyDown={e => {
                        if (e.key === "Enter") {
                            setOpen(false);
                        }
                    }}
                    autoFocus
                />
            )}
            <PopoverAnchor />
            <PopoverContent
                className="w-auto p-0"
                onOpenAutoFocus={e => e.preventDefault()}
                onInteractOutside={e => {
                    if (e.target === inputRef.current) e.preventDefault();
                }}
            >
                <Calendar
                    mode="range"
                    selected={{ from: selectedRange?.from?.toJSDate(), to: selectedRange?.to?.toJSDate() }}
                    onSelect={_range => {
                        if (_range) {
                            let newFrom = _range.from ? DateTime.fromJSDate(_range.from) : undefined;
                            let newTo = _range.to ? DateTime.fromJSDate(_range.to) : undefined;

                            setSelectedRange({ from: newFrom, to: newTo });
                            setInputValue((newFrom ? newFrom.toFormat("dd/MM/yyyy") : "") + " - " + (newTo ? newTo.toFormat("dd/MM/yyyy") : ""));
                            if (onRangeChange && newFrom && newTo) {
                                onRangeChange({ from: newFrom, to: newTo });
                            }
                        }
                    }}
                    month={month}
                    numberOfMonths={2}
                    onMonthChange={setMonth}
                    enableWeekend
                />
            </PopoverContent>
        </Popover>
    );
}
